import { uuidModule } from "@/modules/uuid";
import {
  AssistantChangeRequestChangeKind,
  AssistantChangeRequestStatus,
  AssistantFollowUpChangeRequestModelData,
  AssistantProjectChangeRequestModelData,
  AssistantTaskChangeRequestModelData,
} from "@/store/assistant/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import {
  ISubmitAssistantChangeRequestsOperation,
  ISubmitAssistantFollowUpChangeRequestPayload,
  ISubmitAssistantProjectChangeRequestPayload,
  ISubmitAssistantTaskChangeRequestPayload,
} from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncModelData, SyncOperationKind } from "@/store/sync/types";

export class SubmitAssistantChangeRequestsOperation extends BaseSyncOperation<ISubmitAssistantChangeRequestsOperation> {
  get operationKind(): SyncOperationKind {
    return "SUBMIT_ASSISTANT_CHANGE_REQUESTS";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const requestedAt = this.committedAt;
    const requestedByAccountId = this.store.account.myAccountId;
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    const modelScopes = [generateDefaultOwnerScopes({ store: this.store })];

    const updates: OptimisticSyncUpdate<SyncModelData>[] = [];

    for (const request of this.payload.task_change_requests ?? []) {
      updates.push(
        this.buildTaskUpdate({
          request,
          spaceAccountId,
          requestedAt,
          requestedByAccountId,
          modelScopes,
        })
      );
    }

    for (const request of this.payload.project_change_requests ?? []) {
      updates.push(
        this.buildProjectUpdate({
          request,
          spaceAccountId,
          requestedAt,
          requestedByAccountId,
          modelScopes,
        })
      );
    }

    for (const request of this.payload.follow_up_change_requests ?? []) {
      updates.push(
        this.buildFollowUpUpdate({
          request,
          spaceAccountId,
          requestedAt,
          requestedByAccountId,
          modelScopes,
        })
      );
    }

    return updates;
  }

  async triggerRecompute() {
    const taskRequests = this.payload.task_change_requests ?? [];
    const projectRequests = this.payload.project_change_requests ?? [];
    const followUpRequests = this.payload.follow_up_change_requests ?? [];

    await Promise.all([
      ...taskRequests.map(request => this.store.assistantTaskChangeRequests.recompute(request.id)),
      ...projectRequests.map(request =>
        this.store.assistantProjectChangeRequests.recompute(request.id)
      ),
      ...followUpRequests.map(request =>
        this.store.assistantFollowUpChangeRequests.recompute(request.id)
      ),
    ]);
  }

  private buildTaskUpdate({
    request,
    spaceAccountId,
    requestedAt,
    requestedByAccountId,
    modelScopes,
  }: {
    request: ISubmitAssistantTaskChangeRequestPayload;
    spaceAccountId: string;
    requestedAt: string;
    requestedByAccountId: string;
    modelScopes: ReturnType<typeof generateDefaultOwnerScopes>[];
  }): OptimisticSyncUpdate<AssistantTaskChangeRequestModelData> {
    const modelData: AssistantTaskChangeRequestModelData = {
      space_account_id: spaceAccountId,
      assistant_id: this.payload.assistant_id,
      request_group_id: this.payload.request_group_id,
      requested_by_account_id: requestedByAccountId,
      requested_at: requestedAt,
      change_kind: request.change_kind as AssistantChangeRequestChangeKind,
      status: AssistantChangeRequestStatus.Pending,
      target_task_id: request.target_task_id ?? null,
      requested_title: request.requested_title ?? null,
      requested_description: request.requested_description ?? null,
      requested_status: request.requested_status ?? null,
      requested_due_at: request.requested_due_at ?? null,
      requested_project_id: request.requested_project_id ?? null,
      changed_fields: request.changed_fields ?? [],
      processed_by_run_id: null,
      created_at: requestedAt,
      updated_at: requestedAt,
    };

    return {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: request.id,
        model_kind: "ASSISTANT_TASK_CHANGE_REQUEST",
        model_version: 1,
        model_data: modelData,
        model_scopes: modelScopes,
      },
    };
  }

  private buildProjectUpdate({
    request,
    spaceAccountId,
    requestedAt,
    requestedByAccountId,
    modelScopes,
  }: {
    request: ISubmitAssistantProjectChangeRequestPayload;
    spaceAccountId: string;
    requestedAt: string;
    requestedByAccountId: string;
    modelScopes: ReturnType<typeof generateDefaultOwnerScopes>[];
  }): OptimisticSyncUpdate<AssistantProjectChangeRequestModelData> {
    const modelData: AssistantProjectChangeRequestModelData = {
      space_account_id: spaceAccountId,
      assistant_id: this.payload.assistant_id,
      request_group_id: this.payload.request_group_id,
      requested_by_account_id: requestedByAccountId,
      requested_at: requestedAt,
      change_kind: request.change_kind as AssistantChangeRequestChangeKind,
      status: AssistantChangeRequestStatus.Pending,
      target_project_id: request.target_project_id ?? null,
      requested_title: request.requested_title ?? null,
      requested_description: request.requested_description ?? null,
      requested_status: request.requested_status ?? null,
      changed_fields: request.changed_fields ?? [],
      processed_by_run_id: null,
      created_at: requestedAt,
      updated_at: requestedAt,
    };

    return {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: request.id,
        model_kind: "ASSISTANT_PROJECT_CHANGE_REQUEST",
        model_version: 1,
        model_data: modelData,
        model_scopes: modelScopes,
      },
    };
  }

  private buildFollowUpUpdate({
    request,
    spaceAccountId,
    requestedAt,
    requestedByAccountId,
    modelScopes,
  }: {
    request: ISubmitAssistantFollowUpChangeRequestPayload;
    spaceAccountId: string;
    requestedAt: string;
    requestedByAccountId: string;
    modelScopes: ReturnType<typeof generateDefaultOwnerScopes>[];
  }): OptimisticSyncUpdate<AssistantFollowUpChangeRequestModelData> {
    const modelData: AssistantFollowUpChangeRequestModelData = {
      space_account_id: spaceAccountId,
      assistant_id: this.payload.assistant_id,
      request_group_id: this.payload.request_group_id,
      requested_by_account_id: requestedByAccountId,
      requested_at: requestedAt,
      change_kind: request.change_kind as AssistantChangeRequestChangeKind,
      status: AssistantChangeRequestStatus.Pending,
      target_follow_up_id: request.target_follow_up_id ?? null,
      requested_content: request.requested_content ?? null,
      requested_status: request.requested_status ?? null,
      requested_scheduled_for: request.requested_scheduled_for ?? null,
      requested_task_id: request.requested_task_id ?? null,
      requested_project_id: request.requested_project_id ?? null,
      requested_commitment_kind: request.requested_commitment_kind ?? null,
      requested_due_at: request.requested_due_at ?? null,
      requested_deadline_at: request.requested_deadline_at ?? null,
      requested_trigger_type: request.requested_trigger_type ?? null,
      requested_trigger_not_before: request.requested_trigger_not_before ?? null,
      requested_trigger_not_after: request.requested_trigger_not_after ?? null,
      changed_fields: request.changed_fields ?? [],
      processed_by_run_id: null,
      created_at: requestedAt,
      updated_at: requestedAt,
    };

    return {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: request.id,
        model_kind: "ASSISTANT_FOLLOW_UP_CHANGE_REQUEST",
        model_version: 1,
        model_data: modelData,
        model_scopes: modelScopes,
      },
    };
  }
}
